/* FULL LINEUP — Sequential autoplay of a department's submissions.
 *
 * Founder spec (May 20 2026): "FULL LINEUP is a section where all the
 * videos lineup to create a full movie, sequentially. There will be
 * lineups: LAYOUT LINEUP and ANIMATION LINEUP. These lineups are ONLY
 * meant for internal review — they cannot go to client."
 *
 * Architecture:
 *  • Sequential autoplay (no server-side stitching) — when one clip
 *    ends, we advance to the next. Lightweight and instant.
 *  • Shot-sequence order — sorts by SH_### extracted from parent_code.
 *  • Status toggle — switch between {Approved-only} and {Latest per shot}.
 *  • Watermark "INTERNAL ONLY" overlay on every frame (founder spec).
 *  • Hidden from `client` role — both via the parent gate AND server-
 *    side (the inbox API never returns the lineup as a client-visible
 *    item; this UI just makes it impossible to reach).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, SkipForward, SkipBack, Film, AlertTriangle, Sparkles } from 'lucide-react';

const isVideo = (m) => {
  const fn = (m?.filename || m?.storage_key || '').toLowerCase();
  return (m?.type === 'video') || /\.(mp4|mov|webm|m4v|mkv)$/i.test(fn);
};

// ── Shot-number extractor (founder spec May 20 2026 — clubbed uploads) ──
// Artists upload multi-shot files in one submission (e.g. "SH06, 07, 08, 09, 10"
// with 5 separate .mp4 files attached). The Lineup MUST spread those out
// into individual playlist entries in shot sequence — otherwise a 10-min
// clubbed playblast plays first as one long blob and breaks the rough-cut
// continuity. Filenames always carry the shot number (e.g. "Sh_006.mp4",
// "SH_071_ANM.mp4") so we extract from there per FILE, not per submission.
const SHOT_RE = /\b(?:sh|shot)[\s_-]?(\d{1,4})\b/i;
const shotNumFromFilename = (filename) => {
  if (!filename) return null;
  const m = String(filename).match(SHOT_RE);
  return m ? parseInt(m[1], 10) : null;
};

const shotSeqOf = (s) => {
  const src = String(s.parent_code || s.title || '');
  const m = src.match(SHOT_RE);
  if (m) return parseInt(m[1], 10);
  return 9999;
};

// Explode one submission's media array into PER-FILE playlist entries.
// Each video file → one entry with shot number extracted from filename.
// If no shot number can be parsed, falls back to the submission's
// parent_code/title shot number (matches old behavior for single-file
// uploads where the file is just "v003.mp4" without a SH_### in the name).
const explodeSubmissionToEntries = (sub) => {
  const submissionShotNum = shotSeqOf(sub);
  const videoFiles = (sub.media || []).filter(isVideo);
  if (videoFiles.length === 0) return [];
  // Single-file path: keep as one entry (covers Layout submission, single
  // shot animation playblast, etc).
  if (videoFiles.length === 1) {
    return [{
      key: `${sub.id}__0`,
      submission_id: sub.id,
      media: videoFiles[0],
      shot_num: shotNumFromFilename(videoFiles[0].filename) ?? submissionShotNum,
      shot_label: deriveShotLabel(videoFiles[0].filename, submissionShotNum, sub),
      submitter_name: sub.submitter_name,
      submitter_handle: sub.submitter_handle,
      updated_at: sub.updated_at || sub.created_at,
      status: sub.status,
      producer_decision: sub.producer_decision,
      client_decision: sub.client_decision,
      _is_clubbed: false,
    }];
  }
  // Multi-file path: each file becomes its own entry. This is the
  // founder-spec "spread out clubbed uploads individually" rule.
  return videoFiles.map((m, i) => {
    const num = shotNumFromFilename(m.filename) ?? submissionShotNum;
    return {
      key: `${sub.id}__${i}`,
      submission_id: sub.id,
      media: m,
      shot_num: num,
      shot_label: deriveShotLabel(m.filename, num, sub),
      submitter_name: sub.submitter_name,
      submitter_handle: sub.submitter_handle,
      updated_at: sub.updated_at || sub.created_at,
      status: sub.status,
      producer_decision: sub.producer_decision,
      client_decision: sub.client_decision,
      _is_clubbed: true,
    };
  });
};

const deriveShotLabel = (filename, shotNum, sub) => {
  if (shotNum && shotNum !== 9999) {
    // Pad to SH_### (3 digits) for visual consistency in the player overlay
    return `SH_${String(shotNum).padStart(3, '0')}`;
  }
  return sub?.parent_code || sub?.title || (filename ? filename.split('/').pop() : 'Shot');
};

export default function LineupPlayer({ submissions, departmentLabel, api, mediaUrlFor }) {
  // Status mode toggle: 'latest' (default) shows the freshest per shot;
  // 'approved' shows only producer-approved submissions.
  const [statusMode, setStatusMode] = useState('latest');

  // Build playlist: explode submissions → individual shot files →
  // dedupe by shot number (latest per shot) → sort by shot sequence.
  const playlist = useMemo(() => {
    let pool = submissions || [];
    if (statusMode === 'approved') {
      pool = pool.filter(s => (s.producer_decision || s.client_decision) === 'approved'
        || s.status === 'approved');
    }
    // 1. Flatten — each submission's video files become individual entries.
    const allEntries = [];
    pool.forEach(s => { allEntries.push(...explodeSubmissionToEntries(s)); });

    // 2. Group by shot number → keep the latest entry per shot. This
    //    deduplicates revisions of the same shot AND prevents two
    //    artists' versions of the same shot from both appearing.
    const byShot = new Map();
    allEntries.forEach(e => {
      // Use shot_num when known; entries with no parsable shot fall
      // back to their unique key so they don't collide with each other.
      const groupKey = (e.shot_num && e.shot_num !== 9999) ? `shot:${e.shot_num}` : `key:${e.key}`;
      const ts = new Date(e.updated_at || 0).getTime();
      const prev = byShot.get(groupKey);
      if (!prev || (prev._ts || 0) < ts) {
        byShot.set(groupKey, { ...e, _ts: ts });
      }
    });

    // 3. Sort by shot number (unknown shots last).
    return Array.from(byShot.values())
      .sort((a, b) => (a.shot_num || 9999) - (b.shot_num || 9999));
  }, [submissions, statusMode]);

  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const videoRef = useRef(null);

  // Reset index when playlist changes
  useEffect(() => { setIdx(0); }, [statusMode, submissions]);

  const current = playlist[idx];
  const currentVideoMedia = current?.media || null;

  // Resolve media URL — same auth pattern as ClientReviewPage's MediaCard
  const [src, setSrc] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!currentVideoMedia) { setSrc(''); return; }
    (async () => {
      try {
        const url = await mediaUrlFor(currentVideoMedia);
        if (!cancelled) setSrc(url);
      } catch (_e) {
        if (!cancelled) setSrc('');
      }
    })();
    return () => { cancelled = true; };
  }, [currentVideoMedia, mediaUrlFor]);

  // Autoplay handling
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    if (playing) {
      v.play().catch(() => { /* autoplay may be blocked first time */ });
    } else {
      v.pause();
    }
  }, [playing, src, idx]);

  const advance = () => {
    if (idx < playlist.length - 1) setIdx(i => i + 1);
    else setPlaying(false); // end of lineup
  };

  return (
    <div className="space-y-4" data-testid="lineup-player">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4 text-violet-400"/>
            <h2 className="text-base font-bold text-white">{departmentLabel} · Full Lineup</h2>
            <span className="text-[10px] uppercase tracking-widest text-violet-300 bg-violet-500/15 border border-violet-500/40 rounded px-1.5 py-0.5">Internal Only</span>
          </div>
          <p className="text-[11px] text-zinc-500">
            {playlist.length} shot{playlist.length !== 1 ? 's' : ''} in sequence · auto-plays end-to-end · do NOT share with client
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Show:</span>
          <button
            onClick={() => setStatusMode('latest')}
            data-testid="lineup-mode-latest"
            className={`text-[11px] px-2 py-1 rounded border transition-colors ${statusMode === 'latest' ? 'bg-amber-500/20 text-amber-200 border-amber-500/40' : 'border-zinc-800 text-zinc-400 hover:bg-zinc-900'}`}
          >Latest per shot</button>
          <button
            onClick={() => setStatusMode('approved')}
            data-testid="lineup-mode-approved"
            className={`text-[11px] px-2 py-1 rounded border transition-colors ${statusMode === 'approved' ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40' : 'border-zinc-800 text-zinc-400 hover:bg-zinc-900'}`}
          >Approved only</button>
        </div>
      </div>

      {/* Video player */}
      {playlist.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center" data-testid="lineup-empty">
          <Film className="w-10 h-10 mx-auto text-zinc-700 mb-3"/>
          <p className="text-sm text-zinc-400">No video submissions in this lineup yet.</p>
          <p className="text-[11px] text-zinc-500 mt-1">
            {statusMode === 'approved' ? 'Try the "Latest per shot" toggle to include pending reviews.' : 'Artists need to submit Layout / Animation playblasts first.'}
          </p>
        </div>
      ) : (
        <div className="relative bg-black border border-zinc-800 rounded-md overflow-hidden" data-testid="lineup-video-wrap">
          {/* Watermark — founder spec: every frame says INTERNAL */}
          <div className="absolute top-3 left-3 z-10 pointer-events-none">
            <span className="text-[10px] uppercase tracking-widest text-violet-200 bg-violet-500/20 border border-violet-500/40 rounded px-2 py-0.5 flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3"/> Internal Lineup · Do Not Share
            </span>
          </div>
          {/* Shot label overlay */}
          <div className="absolute bottom-3 left-3 z-10 pointer-events-none">
            <span className="text-[11px] text-white bg-black/70 border border-white/10 rounded px-2 py-1 font-mono">
              {idx + 1} / {playlist.length} · {current?.shot_label || 'Shot'}
              {current?._is_clubbed && <span className="text-amber-300 ml-2 text-[10px]">· from clubbed upload</span>}
              {current?.submitter_handle && <span className="text-zinc-400 ml-2">· @{current.submitter_handle}</span>}
            </span>
          </div>
          {src ? (
            <video
              ref={videoRef}
              src={src}
              autoPlay
              controls
              onEnded={advance}
              onPause={() => setPlaying(false)}
              onPlay={() => setPlaying(true)}
              data-testid="lineup-video"
              className="w-full aspect-video bg-black"
            />
          ) : (
            <div className="aspect-video flex items-center justify-center text-zinc-500 text-sm">Loading {current?.shot_label || ''}…</div>
          )}

          {/* Controls bar */}
          <div className="flex items-center justify-between px-3 py-2 bg-zinc-950/80 border-t border-zinc-800">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIdx(i => Math.max(0, i - 1))}
                disabled={idx === 0}
                data-testid="lineup-prev"
                className="w-8 h-8 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
              ><SkipBack className="w-4 h-4 text-zinc-300"/></button>
              <button
                onClick={() => setPlaying(p => !p)}
                data-testid="lineup-play-pause"
                className="w-8 h-8 rounded hover:bg-zinc-800 flex items-center justify-center"
              >{playing ? <Pause className="w-4 h-4 text-zinc-300"/> : <Play className="w-4 h-4 text-amber-300"/>}</button>
              <button
                onClick={advance}
                disabled={idx >= playlist.length - 1}
                data-testid="lineup-next"
                className="w-8 h-8 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
              ><SkipForward className="w-4 h-4 text-zinc-300"/></button>
            </div>
            <p className="text-[11px] text-zinc-500">
              Plays in shot sequence · click any thumb below to jump
            </p>
          </div>
        </div>
      )}

      {/* Thumbnail strip */}
      {playlist.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="lineup-thumbs">
          {playlist.map((s, i) => (
            <button
              key={s.key || i}
              onClick={() => { setIdx(i); setPlaying(true); }}
              data-testid={`lineup-thumb-${i}`}
              className={`shrink-0 px-2.5 py-2 rounded border text-[10px] text-left transition-colors min-w-[110px] ${
                i === idx ? 'bg-amber-500/20 text-amber-100 border-amber-500/50' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-600'
              }`}>
              <div className="font-mono font-semibold truncate">{s.shot_label || `#${i + 1}`}</div>
              <div className="text-zinc-500 text-[9px] truncate">@{s.submitter_handle || s.submitter_name || '—'}{s._is_clubbed && ' · clubbed'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
